/**
 * One import — the report, and the button — spec §N, T-6.8. Redrawn as a
 * record in Wave 15A (C14 §10.6 item 3, ADR-0033).
 *
 * This is the screen FR-N1's dry run exists for. It shows, per row, what the
 * import **would** do, what it refused and why, and what it will leave alone
 * because it is already here. Only then does it offer the button — the
 * record's one primary action.
 *
 * ## It polls, because the work is in the worker
 *
 * A four-hundred-row client list is minutes against a real database and a real
 * object store, so the upload queues a job and this screen waits on it. The
 * poll stops the moment the job leaves `queued`/`running` — a screen that
 * polled a finished job for ever is a screen that keeps a laptop awake.
 *
 * ## Refused rows come first
 *
 * The default tab is whichever one a firm actually has to act on: the rejects
 * when there are any, and what would be imported when there are none. A report
 * that opens on four hundred green rows and hides the eleven that need fixing
 * behind a tab is a report that gets committed unread.
 */

import { Alert, Button, formatDateTime } from '@fiveox/ui'
import { Download, Loader2, Upload } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import {
  type ListColumn,
  ListTable,
  ListToolbar,
  RecordPage,
  type RecordTab,
  StatusBadge,
  useListTable,
} from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type ImportJob,
  type ImportRow,
  type RowStatus,
  downloadRejects,
  imports,
  isRunning,
  saveBlob,
} from '../../lib/api/imports'
import { statusLabel, statusTone } from './index'

const POLL_MS = 2000

const TABS: Array<{ key: RowStatus; label: string }> = [
  { key: 'rejected', label: 'Refused' },
  { key: 'ready', label: 'Would be imported' },
  { key: 'created', label: 'Imported' },
  { key: 'skipped', label: 'Already here' },
]

const KIND_LABELS: Record<string, string> = {
  clients: 'Clients',
  cases: 'Cases',
  invoices: 'Invoices',
  documents: 'Documents',
  calendar: 'Calendar',
  tags: 'Tags',
}

const ROW_COLUMNS: ListColumn<ImportRow>[] = [
  {
    key: 'row_no',
    label: 'Row',
    width: 64,
    required: true,
    phone: true,
    sortField: 'row_no',
    // The **file's** line number, as a spreadsheet counts it — "row 87" is
    // how a firm finds the line.
    render: (row) => <span className="text-muted-foreground tabular-nums">{row.row_no}</span>,
  },
  {
    key: 'source_ref',
    label: 'Reference',
    width: 140,
    sortField: 'source_ref',
    render: (row) => row.source_ref ?? '—',
  },
  {
    key: 'status',
    label: 'What happens',
    width: 280,
    phone: true,
    render: (row) => (
      // The reason wraps, inside a bounded width: truncating a refusal's
      // reason defeats it, and an unbounded one scrolls it off a phone.
      <span className="flex max-w-[15rem] flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal sm:max-w-xl">
        <StatusBadge kind="import" status={row.status} />
        <span>{row.reason ?? row.summary}</span>
        {row.status === 'created' && typeof row.result.number === 'string' && (
          <span className="text-muted-foreground">{row.result.number}</span>
        )}
      </span>
    ),
  },
]

function RowsTable({ rows, waiting }: { rows: ImportRow[]; waiting: boolean }) {
  const list = useListTable(rows, {
    columns: ROW_COLUMNS,
    search: (row) => [row.source_ref, row.reason, row.summary, row.row_no],
    initialSort: { field: 'row_no' },
    pageSize: 50,
  })
  return (
    <div className="space-y-4">
      {rows.length > 10 && <ListToolbar list={list} placeholder="Search by reference or reason" />}
      <ListTable
        list={list}
        rowKey={(row) => row.id}
        empty={waiting ? 'Waiting for the check to finish…' : 'No rows here.'}
      />
    </div>
  )
}

export function ImportJobDetail() {
  const { jobId = '' } = useParams()
  const [job, setJob] = useState<ImportJob | null>(null)
  const [rows, setRows] = useState<ImportRow[]>([])
  const [tab, setTab] = useState<RowStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const current = (await imports.read(jobId)).data
    setJob(current)
    if (!isRunning(current)) {
      setRows((await imports.rows(jobId)).data)
    }
    return current
  }, [jobId])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      try {
        const current = await load()
        if (cancelled) return
        // Stops the moment the worker is done with it.
        if (isRunning(current)) timer = setTimeout(tick, POLL_MS)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'That import could not be read.')
        }
      }
    }
    void tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [load])

  // The tab a firm has to act on, chosen once the counts are known and never
  // fought with afterwards — `tab` is null until they pick one.
  const counts: Record<RowStatus, number> = {
    rejected: job?.rejected_rows ?? 0,
    ready: job?.ready_rows ?? 0,
    created: job?.created_rows ?? 0,
    skipped: job?.skipped_rows ?? 0,
  }
  const suggested: RowStatus =
    counts.rejected > 0 ? 'rejected' : counts.ready > 0 ? 'ready' : 'created'
  const showing = tab ?? suggested

  const byStatus = useMemo(() => {
    const out: Record<RowStatus, ImportRow[]> = { rejected: [], ready: [], created: [], skipped: [] }
    for (const row of rows) out[row.status]?.push(row)
    return out
  }, [rows])

  async function onCommit() {
    setBusy(true)
    setError(null)
    try {
      setJob((await imports.commit(jobId)).data)
      setRows([])
      setTab(null)
      // The poll in the effect above is keyed on `load`, which does not change;
      // this kicks it back into life for the commit pass.
      const current = await load()
      if (isRunning(current)) setTimeout(() => void load(), POLL_MS)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That import could not be started.')
    } finally {
      setBusy(false)
    }
  }

  async function onRejects() {
    try {
      saveBlob(await downloadRejects(jobId), `rejects-${jobId}.csv`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That report could not be downloaded.')
    }
  }

  const back = { to: '/imports', label: 'All imports' }

  if (!job) {
    return <RecordPage back={back} title="Import" loading error={error} />
  }

  // Only the tabs that hold rows; the one showing is always among them.
  const visibleTabs = TABS.filter((entry) => counts[entry.key] > 0 || entry.key === showing)
  const tabs: RecordTab[] = visibleTabs.map((entry) => ({
    id: entry.key,
    label: entry.label,
    badge: counts[entry.key],
    content: (
      <div className="space-y-3">
        {entry.key === 'rejected' && counts.rejected > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-muted-foreground text-sm">
              Correct these in your file and upload it again — the rest will be recognised as
              already here.
            </p>
            <Button variant="outline" size="sm" onClick={() => void onRejects()}>
              <Download size={14} aria-hidden /> Download the refused rows
            </Button>
          </div>
        )}
        <RowsTable rows={byStatus[entry.key]} waiting={isRunning(job)} />
      </div>
    ),
  }))

  return (
    <RecordPage
      back={back}
      title={job.filename}
      status={{
        kind: 'import',
        status: job.status,
        label: statusLabel(job),
        // A committed import with refusals is a warning (see `statusTone`).
        tone: statusTone(job),
      }}
      facts={[
        { label: 'What', value: KIND_LABELS[job.kind] ?? job.kind },
        { label: 'Rows', value: job.total_rows },
        ...(job.created_at ? [{ label: 'Started', value: formatDateTime(job.created_at) }] : []),
      ]}
      primary={
        job.can_commit ? (
          <Button onClick={() => void onCommit()} disabled={busy}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Import {job.ready_rows} {job.ready_rows === 1 ? 'row' : 'rows'}
          </Button>
        ) : undefined
      }
      more={
        job.rejected_rows > 0
          ? [
              {
                id: 'rejects',
                label: 'Download the refused rows',
                icon: <Download size={14} aria-hidden />,
                onSelect: () => void onRejects(),
              },
            ]
          : undefined
      }
      error={error}
      tabs={tabs}
      tab={showing}
      onTabChange={(id) => setTab(id as RowStatus)}
    >
      {job.status === 'failed' && (
        <Alert tone="error">
          {job.error}
          {job.error_code === 'USAGE_LIMIT_EXCEEDED' && (
            <span className="mt-1 block text-xs">
              You were told at the preview rather than part-way through the import, which is why
              nothing has changed.
            </span>
          )}
        </Alert>
      )}

      {isRunning(job) && (
        <Alert tone="info">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            {job.mode === 'commit'
              ? 'Importing. This page updates itself.'
              : 'Checking the file. Nothing has been imported.'}
          </span>
        </Alert>
      )}

      {job.status === 'previewed' && (
        <Alert tone={job.ready_rows > 0 ? 'info' : 'warning'}>
          {job.ready_rows > 0 ? (
            <>
              Nothing has been imported yet. {job.ready_rows} of {job.total_rows} rows would be
              imported
              {job.rejected_rows > 0 && `, and ${job.rejected_rows} would be refused`}
              {job.skipped_rows > 0 && `, and ${job.skipped_rows} are already here`}.
            </>
          ) : (
            <>
              None of these rows can be imported. Download the refused rows, correct them and
              upload the file again.
            </>
          )}
        </Alert>
      )}
    </RecordPage>
  )
}
